import classNames from 'classnames';
import marked from 'marked';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useUserCentricsConsentEvent } from '@toolbox/hooks/useUserCentricsConsentEvent';

import { Layer } from '@graphql';
import { FAQSection } from '@components/FAQSection';
import { WidgetWrapper } from '@components/Widget';
import { BreadCrumb } from '@components/BreadCrumb';
import { GradientScale } from '@components/GradientScale';
import { GetInTouch } from '@components/GetInTouch';
import { SearchBar } from '@components/SearchBar';
import { ReviewBar } from '@components/ReviewBar';
import { PriceBox } from '@components/PriceBox';
import Typography from '@components/Typography';
import { Chart } from '@components/Chart';
import { Button } from '@components/Button';
import { breadCrumbElementsProps, generateBreadCrumbPropsFromLayer, defined } from '@lib/utils';
import { defaultTheme } from '@lib/themes';
import getLayerStore from '@stores/LayerStore';
import { Footer } from '@components/Footer';
import { ExpandTapper } from '@components/ExpandTapper';
import UpArrowIcon from '@static/icons/up-arrow.png';
import { ContentSectionWrapper } from './styles';
import { TextBlock } from './TextBlock';
import { NearByAreas } from './nearByAreas';
import { PriceTableWithoutRent } from '../PriceTableWithoutRent';

import type { FAQSectionData } from '@components/FAQSection';
import type { PriceTableType } from '@stores/LayerStore';
import type { GetInfoByArea, LayerInfo } from '@lib/types';
import type {
  GetInfoByAreaSubAreas,
  GetInfoByAreaTopCities,
  GetNearbyAreasNearbyAreas,
} from '@graphql';
import type { PriceData } from '@components/PriceTable';

interface Props {
  layer: LayerInfo;
  layerData: GetInfoByArea;
  faqData?: FAQSectionData;
}

const LayerStore = getLayerStore();

export const ContentSection = observer(({ layer, layerData, faqData }: Props) => {
  const [expandContent, setExpandContent] = useState(true);
  const [mapsConsentIsGiven, setMapsConsentIsGiven] = useState(false);
  const { infoByArea } = layerData;
  const contentSectionRef = useRef<HTMLDivElement>(null);

  if (infoByArea === null) {
    return <ContentSectionWrapper>no data</ContentSectionWrapper>;
  }

  useUserCentricsConsentEvent((event) => {
    if (event.detail['Google Maps']) {
      setMapsConsentIsGiven(true);
    }
  });

  useEffect(() => {
    const handleRouteChangeComplete = (): void => {
      setExpandContent(true);
      if (process.browser && contentSectionRef.current) {
        if (contentSectionRef.current.scrollTo) {
          contentSectionRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } else {
          contentSectionRef.current.scrollTop = 0;
        }
      }
    };

    document.addEventListener('mcm:pushRouteComplete', handleRouteChangeComplete);

    return () => {
      document.removeEventListener('mcm:pushRouteComplete', handleRouteChangeComplete);
    };
  }, []);

  const mapPriceList = (
    data: GetInfoByAreaSubAreas | GetInfoByAreaTopCities | GetNearbyAreasNearbyAreas,
  ): PriceData | null => {
    const url = data.url;
    const name = 'name' in data ? data.name : data.cityName || data.stateName;

    if (url == null || name == null) {
      return null;
    }

    return {
      url,
      name,
      avgPriceApartmentBuy: data.avgPriceApartmentBuy,
      avgPriceHouseBuy: data.avgPriceHouseBuy,
      avgPriceApartmentRent: data.avgPriceApartmentRent,
      avgPriceHouseRent: data.avgPriceHouseRent,
    };
  };

  const loadMoreByType = (type: PriceTableType): void => {
    if (LayerStore.isNextPageExist(type)) {
      LayerStore.priceTableNextPage(type);
      return;
    }
    LayerStore.resetPage(type);
  };

  const titleOfPriceTable = (blackList = false): string => {
    switch (layer.layerType) {
      case Layer.Country:
        return blackList
          ? 'Quadratmeterpreise für andere Bundesländer'
          : 'Quadratmeterpreise für alle Bundesländer';
      case Layer.State:
        return blackList
          ? `Quadratmeterpreise für andere große Städte in ${layer.state || layer.layerName}`
          : `Quadratmeterpreise für die größten Städte in ${layer.state || layer.layerName}`;
      case Layer.City:
        return blackList
          ? `Quadratmeterpreise für andere Stadtteile in ${layer.city || layer.layerName}`
          : `Quadratmeterpreise für Stadtteile in ${layer.city || layer.layerName}`;
      default:
        return blackList
          ? `Quadratmeterpreise für andere angrenzende Stadtteile an ${
              layer.district || layer.layerName
            }`
          : `Quadratmeterpreise für angrenzende Stadtteile an ${layer.district || layer.layerName}`;
    }
  };

  const handleExpand = (): void => {
    setExpandContent(true);
  };

  const handleCollapse = (): void => {
    setExpandContent(false);
  };

  const breadCrumbProps = generateBreadCrumbPropsFromLayer(layer);

  return (
    <ContentSectionWrapper
      className={classNames({
        'position-selected': LayerStore.positionSelected,
        expanded: expandContent,
      })}
      ref={contentSectionRef}
    >
      <ExpandTapper className="expand-tap" onClick={handleExpand}>
        <img src={UpArrowIcon} alt="up-arrow" />
        <Typography type="B2">Tippen, um auszuklappen</Typography>
      </ExpandTapper>
      <Button className="collapse-button" onClick={handleCollapse}></Button>
      <GradientScale className="show-on-mobile gradient-scale" />
      {layer.layerType === Layer.Country && (
        <Typography type="H1" className="h1" as="h1">
          Finden Sie heraus was Ihre Immobilie wert ist!
        </Typography>
      )}
      {layer.layerType !== Layer.Country && (
        <BreadCrumb values={breadCrumbElementsProps(breadCrumbProps)} />
      )}
      {mapsConsentIsGiven && <SearchBar />}
      {layer.layerType === Layer.Country && (
        <p className="bottom-title">
          Berechnung auf Grundlage von <span>35 Millionen </span>Vergleichsdaten
        </p>
      )}
      {layer.layerType !== Layer.Country && <PriceBox isExpanded={expandContent} layer={layer} />}
      {layer.layerType === Layer.Country && <ReviewBar />}
      {layer.layerType !== Layer.Country && LayerStore.trendsData.length > 1 && (
        <div>
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />
          <Chart
            title={`Verkaufspreis-Entwicklung in ${layer.layerName}`}
            trends={LayerStore.trendsData}
          />
        </div>
      )}
      {infoByArea.description && layer.layerType !== Layer.Country && (
        <TextBlock>
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />
          <h2>Informationen zu den Immobilienpreisen in {infoByArea.name}</h2>
          <div
            dangerouslySetInnerHTML={{
              __html: marked(infoByArea.description, { breaks: true }),
            }}
          />
        </TextBlock>
      )}
      {LayerStore.pricesTable.topCitiesWL.count > 0 && layer.layerType === Layer.Country && (
        <div>
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />

          <PriceTableWithoutRent
            title={`Quadratmeterpreise für die größten deutschen Städte`}
            tableHeadline="Stadt"
            priceList={LayerStore.pricesTable.topCitiesWL.paginatedData
              .map(mapPriceList)
              .filter(defined)}
            loadMore={() => loadMoreByType('topCitiesWL')}
            isNextPageExist={LayerStore.isNextPageExist('topCitiesWL')}
            singlePage={
              LayerStore.pricesTable.topCitiesWL.count <= LayerStore.pricesTable.topCitiesWL.perPage
            }
          />
        </div>
      )}
      {layer.layerType !== Layer.Country && LayerStore.pricesTable.priceListWL.count > 0 && (
        <div>
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />
          <PriceTableWithoutRent
            title={titleOfPriceTable()}
            tableHeadline={layer.layerType === Layer.State ? 'Stadt' : 'Stadtteil'}
            priceList={LayerStore.pricesTable.priceListWL.paginatedData
              .map(mapPriceList)
              .filter(defined)}
            loadMore={() => loadMoreByType('priceListWL')}
            isNextPageExist={LayerStore.isNextPageExist('priceListWL')}
            singlePage={
              LayerStore.pricesTable.priceListWL.count <= LayerStore.pricesTable.priceListWL.perPage
            }
          />
        </div>
      )}
      {layer.layerType !== Layer.Country && LayerStore.pricesTable.priceListBL.count > 0 && (
        <div>
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />

          <PriceTableWithoutRent
            title={titleOfPriceTable(true)}
            tableHeadline={layer.layerType === Layer.State ? 'Stadt' : 'Stadtteil'}
            priceList={LayerStore.pricesTable.priceListBL.paginatedData
              .map(mapPriceList)
              .filter(defined)}
            loadMore={() => loadMoreByType('priceListBL')}
            isNextPageExist={LayerStore.isNextPageExist('priceListBL')}
            singlePage={
              LayerStore.pricesTable.priceListBL.count <= LayerStore.pricesTable.priceListBL.perPage
            }
            withoutLink={true}
          />
        </div>
      )}
      {(layer.layerType === Layer.District || layer.layerType === Layer.DistrictSmall) && (
        <>
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />
          <PriceTableWithoutRent
            title={`Quadratmeterpreise für angrenzende Stadtteile an ${layer.layerName}`}
            tableHeadline="Stadtteil"
            priceList={LayerStore.pricesTable.nearbyDistrictsWL.paginatedData
              .map(mapPriceList)
              .filter(defined)}
            loadMore={() => loadMoreByType('nearbyDistrictsWL')}
            isNextPageExist={LayerStore.isNextPageExist('nearbyDistrictsWL')}
            singlePage={
              LayerStore.pricesTable.nearbyDistrictsWL.count <=
              LayerStore.pricesTable.nearbyDistrictsWL.perPage
            }
          />
          <PriceTableWithoutRent
            title={`Quadratmeterpreise für andere angrenzende Stadtteile an ${layer.layerName}`}
            tableHeadline="Stadtteil"
            priceList={LayerStore.pricesTable.nearbyDistrictsBL.paginatedData
              .map(mapPriceList)
              .filter(defined)}
            loadMore={() => loadMoreByType('nearbyDistrictsBL')}
            isNextPageExist={LayerStore.isNextPageExist('nearbyDistrictsBL')}
            singlePage={
              LayerStore.pricesTable.nearbyDistrictsBL.count <=
              LayerStore.pricesTable.nearbyDistrictsBL.perPage
            }
            withoutLink={true}
          />
        </>
      )}
      {layer.layerType !== Layer.Country && (
        <div>
          <WidgetWrapper layer={layer} />
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />
          <GetInTouch
            title="Sie möchten Ihre Immobilie verkaufen?"
            buttonText="Kundenberatung anfordern"
          />
        </div>
      )}
      {infoByArea.seoDescription && (
        <TextBlock>
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />
          <h2>Über {infoByArea.name}</h2>
          <div
            dangerouslySetInnerHTML={{
              __html: marked(infoByArea.seoDescription, { breaks: true }),
            }}
          />
        </TextBlock>
      )}
      {layer.layerType !== Layer.Country && (
        <div>
          <hr color={defaultTheme.colors.grey._300} className="seperate-line" />
          <NearByAreas data={LayerStore.pricesTable.topCitiesWL.originalData} />
        </div>
      )}
      {faqData && (
        <div>
          <FAQSection header={faqData.headerText} items={faqData.items} />
        </div>
      )}
      <Footer className="show-on-mobile" />
    </ContentSectionWrapper>
  );
});
